<template>
  <BOverlay
    :show="loading"
    spinner-variant="primary"
    variant="light"
    blur="0"
    opacity=".5"
    rounded="sm"
  >
    <div
      id="table"
      :class="items.length > 20 && 'scrolled-table'"
      @scroll="handleScroll"
    >
      <b-table-simple
        id="data-partner-table"
        bordered
        responsive
        class="border border-solid rounded-lg"
      >
        <b-thead>
          <!-- First header row: Group headers -->
          <b-tr>
            <b-th
              rowspan="2"
              class="text-center align-middle"
            >
              Waktu Registrasi
            </b-th>
            <b-th
              rowspan="2"
              class="text-center align-middle !border-r-[transparent]"
            >
              Nama Akun
            </b-th>
            <b-th
              rowspan="2"
              class="text-center align-middle"
            >
              No. Handphone
            </b-th>
            <b-th
              colspan="3"
              class="text-center"
            >
              Whitelisted
            </b-th>
            <b-th
              rowspan="2"
              class="text-center align-middle !border-r-[transparent]"
            >
              Total Spending
            </b-th>
            <b-th
              rowspan="2"
              class="text-center align-middle"
            >Aksi</b-th>
          </b-tr>
          <!-- Second header row: Sub headers -->
          <b-tr>
            <b-th class="text-center align-middle place-items-center">
              <img
                src="https://storage.googleapis.com/komerce/komads-meta/meta.svg"
                alt="Meta"
                width="24"
              >
            </b-th>
            <b-th class="text-center align-middle place-items-center">
              <img
                src="https://storage.googleapis.com/komerce/komads-meta/google.svg"
                alt="Google"
                width="24"
              >
            </b-th>
            <b-th class="text-center align-middle place-items-center">
              <img
                src="https://storage.googleapis.com/komerce/komads-meta/tiktok.svg"
                alt="TikTok"
                width="24"
              >
            </b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr
            v-for="(item, index) in items"
            :key="item.id || index"
          >
            <!-- Waktu Registrasi -->
            <b-td class="text-center">
              <div class="min-w-[120px] text-left">
                <p class="mb-0 text-black">
                  {{ getSeparateDateTime(item.created_at).date }}
                </p>
                <p class="mb-0">
                  {{ getSeparateDateTime(item.created_at).time }}
                </p>
              </div>
            </b-td>
            <!-- Nama -->
            <b-td class="!border-r-[transparent]">
              <div class="flex flex-col">
                <div class="flex items-center gap-x-3">
                  <img
                    v-if="item.user_badge"
                    :src="item.user_badge"
                    alt="User Level"
                    width="20"
                  >
                  <p class="max-w-[300px] m-0">
                    <span class="text-[#333333] text-ellipsis line-clamp-1">{{ item.full_name }}</span>
                  </p>
                  <img
                    v-if="item.is_verified_ktp"
                    src="https://storage.googleapis.com/komerce/assets/icons/verified.svg"
                    alt="verified"
                    width="20"
                  >
                </div>
                <span class="text-[#828282] text-sm">{{
                  item.email
                }}</span>
              </div>
            </b-td>

            <!-- No HP -->
            <b-td>
              <div
                v-if="item.phone_number"
                class="text-[#333333] text-center"
              >
                {{ item.phone_number }}
              </div>
              <div v-else>
                -
              </div>
            </b-td>

            <!-- Meta Whitelist -->
            <b-td class="text-center">
              <div class="flex items-center justify-center gap-2 min-w-[120px]">
                <span
                  :id="`meta-tooltip-${index}`"
                  class="text-center"
                >
                  {{ getWhitelistDataPlatform(item, 'meta').total_spending }}
                </span>
                <b-tooltip
                  :target="`meta-tooltip-${index}`"
                  placement="topright"
                  triggers="hover"
                  variant="light"
                >
                  <div class="grid grid-cols-[16px_1fr_1fr] gap-y-[.5rem] gap-x-[.25rem] items-center text-left">
                    <span class="size-[14px] bg-[#08A0F7] rounded-full" />
                    <span>Top Up Fee</span>
                    <span class="font-semibold ms-[10px]">{{ getWhitelistDataPlatform(item, 'meta').topup_fee_percentage }}</span>
                    <span class="size-[14px] bg-[#FBBC05] rounded-full" />
                    <span>Revenue</span>
                    <span class="font-semibold ms-[10px] whitespace-nowrap">{{ getWhitelistDataPlatform(item, 'meta').revenue }}</span>
                  </div>
                </b-tooltip>
              </div>
            </b-td>

            <!-- Google Whitelist -->
            <b-td class="text-center">
              <div class="flex items-center justify-center gap-2 min-w-[120px]">
                <span
                  :id="`google-tooltip-${index}`"
                  class="text-center"
                >
                  {{ getWhitelistDataPlatform(item, 'google').total_spending }}
                </span>
                <b-tooltip
                  :target="`google-tooltip-${index}`"
                  placement="topright"
                  triggers="hover"
                  variant="light"
                >
                  <div class="grid grid-cols-[16px_1fr_1fr] gap-y-[.5rem] gap-x-[.25rem] items-center text-left">
                    <span class="size-[14px] bg-[#08A0F7] rounded-full" />
                    <span>Top Up Fee</span>
                    <span class="font-semibold ms-[10px]">{{ getWhitelistDataPlatform(item, 'google').topup_fee_percentage }}</span>
                    <span class="size-[14px] bg-[#FBBC05] rounded-full" />
                    <span>Revenue</span>
                    <span class="font-semibold ms-[10px] whitespace-nowrap">{{ getWhitelistDataPlatform(item, 'google').revenue }}</span>
                  </div>
                </b-tooltip>
              </div>
            </b-td>

            <!-- TikTok Whitelist -->
            <b-td class="text-center">
              <div class="flex items-center justify-center gap-2 min-w-[120px] w-full">
                <span
                  :id="`tiktok-tooltip-${index}`"
                  class="text-center"
                >
                  {{ getWhitelistDataPlatform(item, 'tiktok').total_spending }}
                </span>
                <b-tooltip
                  :target="`tiktok-tooltip-${index}`"
                  placement="topright"
                  triggers="hover"
                  variant="light"
                >
                  <div class="grid grid-cols-[16px_1fr_1fr] gap-y-[.5rem] gap-x-[.25rem] items-center text-left">
                    <span class="size-[14px] bg-[#08A0F7] rounded-full" />
                    <span>Top Up Fee</span>
                    <span class="font-semibold ms-[10px]">{{ getWhitelistDataPlatform(item, 'tiktok').topup_fee_percentage }}</span>
                    <span class="size-[14px] bg-[#FBBC05] rounded-full" />
                    <span>Revenue</span>
                    <span class="font-semibold ms-[10px] whitespace-nowrap">{{ getWhitelistDataPlatform(item, 'tiktok').revenue }}</span>
                  </div>
                </b-tooltip>
              </div>
            </b-td>

            <!-- Total Spending -->
            <b-td class="text-left !border-r-[transparent]">
              <div class="min-w-[120px]">
                <span>{{ item.total_overall_spending !== null ? IDR(item.total_overall_spending) : '-' }}</span>
              </div>
            </b-td>

            <!-- Detail -->
            <b-td class="text-center">
              <div class="flex justify-center min-w-[100px]">
                <BButton
                  variant="link"
                  class="!text-primary p-0"
                  @click="viewDetail(item.id)"
                >
                  Detail
                </BButton>
              </div>
            </b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>
    </div>
  </BOverlay>
</template>

<script>
// eslint-disable camelcase
import { FULL_DATE_TIME_WITH_WIB } from '@/libs/filterDate'
import { IDR } from '@/libs/currency'

export default {
  name: 'DataPartnerTable',
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      IDR,
      getSeparateDateTime(stringDate) {
        const [date, time] = FULL_DATE_TIME_WITH_WIB(stringDate).split(' | ')
        return { date, time }
      },
    }
  },
  methods: {
    /**
     * Get whitelist data for specific platform from item
     * @param {Object} items - The data item
     * @param {String} platform - Platform name (meta, google, tiktok)
     * @returns {Object|null} - Whitelist data object or null if not found
     */
    getWhitelistData(items, platform) {
      // Check if whitelist_data exists and is an array
      if (!items.whitelist_data || !Array.isArray(items.whitelist_data)) {
        return null
      }

      return items.whitelist_data.find(item => item.platform_name === platform)
    },

    /**
     * Return short status text for per-platform whitelist
     * @param {Object} items - The data item
     * @param {String} platform - Platform name (meta, google, tiktok)
     * @returns {String} - Status text
     */
    getWhitelistShortStatus(items, platform) {
      const whitelistData = this.getWhitelistData(items, platform)

      const { whitelist_status } = whitelistData
      const status = {
        not_active: 'Nonaktif',
        'on review': 'Review',
        accepted: 'Active',
        rejected: 'Nonaktif',
      }

      return status[whitelist_status]
    },

    /**
     * Get CSS class for whitelist status badge
     * @param {Object} items - The data item
     * @param {String} platform - Platform name (meta, google, tiktok)
     * @returns {String} - CSS class string
     */
    getWhitelistBadgeClass(items, platform) {
      const whitelistData = this.getWhitelistData(items, platform)

      const { whitelist_status } = whitelistData
      const badge = {
        not_active: 'bg-gray-100 text-[#828282]',
        'on review': 'bg-blue-100 text-[#08A0F7]',
        accepted: 'bg-[#E9F9F1] text-[#34A770]',
        rejected: 'bg-gray-100 text-[#828282]',
      }
      return badge[whitelist_status]
    },

    /**
     * Get data table for cell and tooltip display
     * @param {Object} item - The data item
     * @param {String} platform - Platform name (meta, google, tiktok)
     * @returns {Object}
     */
    getWhitelistDataPlatform(item, platform) {
      const whitelist = this.getWhitelistData(item, platform)
      return {
        total_spending: whitelist.total_spending !== null ? IDR(whitelist.total_spending) : '-',
        revenue: whitelist.revenue !== null ? IDR(whitelist.revenue) : '-',
        topup_fee_percentage: whitelist.topup_fee_percentage !== null ? `${whitelist.topup_fee_percentage}%` : '-',
      }
    },

    viewDetail(id) {
      this.$emit('view-detail', id)
    },

    handleScroll(event) {
      this.$emit('scroll', event)
    },
  },
}
</script>

<style scoped>
.scrolled-table {
  height: calc(100vh - 250px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  position: relative;
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.scrolled-table .table {
  position: relative;
  border-collapse: separate;
  width: 100%;
  table-layout: fixed;
  margin-bottom: 0;
}

/* First header row (group headers) */
#data-partner-table thead tr:first-child th {
  top: 0;
  font-weight: 400;
  padding: 8px;
  text-transform: capitalize;
  color: #0A0A0A;
}

/* Second header row (sub headers) */
#data-partner-table thead tr:nth-child(2) th {
  padding: 8px;
  font-weight: normal;
}

/* Table cell styling */
#data-partner-table td {
  padding: 12px 8px;
  vertical-align: middle;
}

#data-partner-table th {
  color: #333333;
  font-size: 14px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .scrolled-table {
    height: calc(100vh - 200px);
  }

  #data-partner-table thead tr:nth-child(2) th {
    top: 35px;
  }
}
</style>
